import { motion } from "framer-motion";

import { HeroSystem } from "../components/HeroSystem";
import { ButtonLink } from "../components/ui";
import { site } from "../data/site";

/**
 * The five-second answer.
 *
 * The headline deliberately avoids "we build technology that moves
 * businesses forward" — that sentence is true of every company that has
 * ever existed, which is what makes it invisible. "Hard problems" and
 * "production" are both load-bearing: the first says which work we want,
 * the second says we finish it. A CTO in Riyadh reads it once and knows.
 */
export function Hero() {
  return (
    <section className="relative pt-14 sm:pt-20 lg:pt-28">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[420px] w-[860px] -translate-x-1/2 rounded-full bg-brand-emerald/12 blur-[140px]" />
      <div className="pointer-events-none absolute right-[6%] top-[18%] -z-10 size-64 rounded-full bg-brand-violet/20 blur-[120px]" />

      <div className="container">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div>
            <motion.div
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-white/60"
            >
              A technology company · {site.locations.join(" · ")}
            </motion.div>

            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.65, delay: 0.05 }}
              className="mt-5 font-display text-[2.35rem] font-bold leading-[1.04] tracking-tight sm:mt-6 sm:text-[3.25rem] lg:text-[3.6rem]"
            >
              We turn hard problems into{" "}
              <span className="text-gradient">production software.</span>
            </motion.h1>

            <motion.p
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.65, delay: 0.14 }}
              className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/70 sm:mt-6 sm:text-lg"
            >
              AI strategy, software engineering, and our own SaaS products. We
              come in at any point between the first architecture decision and
              the system running under load — and we have walked that whole
              line ourselves, on our own product.
            </motion.p>

            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.22 }}
              className="mt-7 flex flex-col items-stretch gap-3 sm:mt-9 sm:flex-row sm:items-center"
            >
              <ButtonLink href="/contact">Start a project</ButtonLink>
              <ButtonLink href="/work" variant="ghost" icon={false}>
                Explore our work
              </ButtonLink>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.36 }}
              className="mt-6 text-xs text-white/40"
            >
              Hazri Book is in production in {site.productionMarkets[0]}.
              Agent Labista is in development. Engineering built for the GCC,
              India, the UK, Singapore and the US.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
          >
            <HeroSystem />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
